import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { IdentificationResidence, switchIdentificationResidence } from '@/features/checkIn/identification-slice'
import { useEffect } from 'react'
import { useHotelId } from './use-hotel-id'

/**
 * 本人確認のページの状態。
 *
 * `loading` の間はページ側で何も描画しない。施設設定は `identity-verify-loader` が
 * ページに入る前に揃えるので、実際に待つのはアカウント情報だけ。
 * 対象外のゲストをここから追い出す責務は持たない。
 */
export type IdentityVerifyScreen =
  | { status: 'loading' }
  | {
      status: 'ready'
      hotelId: string
      requiredIdentifyVerification: 0 | 1
      isSkippable: boolean
      residence: IdentificationResidence
      selectResidence: (residence: IdentificationResidence) => void
    }

/**
 * 本人確認のページの振る舞い。
 *
 * - 施設設定から `requiredIdentifyVerification` / `isPreCheckinIdentifySkippable` を取り出す
 * - 在住地を決める。まだ選ばれていなければゲストの登録上の在住地を初期値にし、
 *   store（`checkIn.identification.residence`）に確定させる
 *
 * このゲストが本人確認の対象かどうかの判断は `src/loaders/identity-verify-loader.ts` が持つ。
 */
export const useIdentityVerifyScreen = (): IdentityVerifyScreen => {
  const dispatch = useAppDispatch()
  const { getHotelId } = useHotelId()

  const checkInSetting = useAppSelector(state => state.checkInSetting.checkInSetting)
  const accountInfo = useAppSelector(state => state.accountInfo.user)
  const selectedResidence = useAppSelector(state => state.checkIn.identification.residence)
  // 画像のアップロード先は、URL のクエリで変わる施設 ID ではなくこのチェックインの施設
  const checkInHotelId = useAppSelector(state => state.checkIn.checkInData.fields.hotelId) || (getHotelId() ?? '')

  const isAccountSettled = !!accountInfo?.nationality

  // まだ選ばれていなければゲストの登録上の在住地を初期値にする。
  // タブでもう一方を選べるので、これで出せる書類が決まるわけではない
  const residence: IdentificationResidence = selectedResidence ?? (accountInfo?.nationality === 'JPN' ? 'japanese' : 'overseas')

  useEffect(() => {
    if (!isAccountSettled || selectedResidence) {
      return
    }
    // 初期値を、この画面に入った時点で store に確定させる。
    // 「次へ」を押すまで待つと、アップロードし終えたあとに切り替わり扱いになって
    // 自分自身の画像まで捨ててしまう（`switchIdentificationResidence` は
    // 「前回と違う在住地」に切り替わったら画像を全部捨てる実装のため）
    dispatch(switchIdentificationResidence({ residence }))
  }, [dispatch, isAccountSettled, selectedResidence, residence])

  if (!checkInSetting || !isAccountSettled) {
    return { status: 'loading' }
  }

  return {
    status: 'ready',
    hotelId: checkInHotelId,
    requiredIdentifyVerification: checkInSetting.requiredIdentifyVerification ?? 0,
    isSkippable: checkInSetting.isPreCheckinIdentifySkippable === 1,
    residence,
    selectResidence: next => dispatch(switchIdentificationResidence({ residence: next })),
  }
}
